/**
 * 69.58 (ARCHITECTURE §5.29; DECISIONS 270 (3), 271 (7), 272 (1)): a ship's countermeasures against the missiles after it.
 *
 * **Decoys** (GameConfig.countermeasures): a ship carries salvos of flares (`decoys`, COUNTERMEASURES.load at its call). A bot
 * pilot pops one at a missile after it once the missile is within reach — one salvo a missile, the nearest not yet answered,
 * at most one every popSeconds. Whether the seeker takes the flare is decided once, at the pop, and closed-form (272 (1): "I
 * think geometry … then also some probability of flare countermeasure distracting … don't want it to be too spendy"):
 * - **geometry:** a moment on (`lookSeconds`), the flare must be in the seeker's view beside the ship — within its class's
 *   lock cone of the ship as the missile sees it; popped too late (the missile on the ship within that moment), never — and
 *   the aspect, the flare against the engine: chased from behind, the seeker sees the engine's heat beside the flare
 *   (`aspectTail` of the chance, dead astern); head-on the engine is hidden and the flare stands out (the whole chance);
 * - **the IR chance** on top: the missile's class's `seduce`, rolled keyed by the seed, the missile, the salvo and the tick.
 * Taken, the missile's target becomes the flare (its id in the decoys' range): it chases it and bursts on it, or flies straight
 * once it burns out. No radio frequency is modelled (no chaff). A flare is a closed-form fall from its pop (`decoyAt`):
 * nothing is stepped.
 *
 * **The gunship's turret** (GameConfig.shipTurrets; GUNSHIP.turret): a burst every interval at the nearest missile after its
 * ship within its reach, a kill by point defence's chance (core/pointDefence.ts killChance, the missile a rocket), from a drum.
 *
 * Point defence's guns are not fooled by flares. Pure and deterministic; nothing runs with neither switch on.
 */
import type { DecoyFlare, DropshipFlight, GameState, MissileFlight, Vec3 } from '../types';
import { COUNTERMEASURES, DECOY_ID_BASE } from '../constants/action';
import { MISSILE_CLASSES } from '../constants/weapons';
import { GUNSHIP } from '../constants/gunship';
import { PHYS_DT } from '../constants/physics';
import { SeededRandom } from './random';
import { flightNow } from './shipNow';
import { killChance } from './pointDefence';
import { explosionBlastFor } from './explosions';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { flareAt } from './decoys';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos } from './dmath';
import { defaultOn } from './defaultOn';

const C = COUNTERMEASURES;
const personOf = (state: Pick<GameState, 'config'>): number => PERSON_BODY_HEIGHT * personSetOf(state).k;
const ticksOf = (seconds: number): number => Math.max(1, Math.round(seconds / PHYS_DT));

/**
 * The chance a flare popped from a ship at `ship` (its hull and velocity) takes missile `m`: 0 when the missile meets the ship
 * within `lookSeconds` (too late), or unless, `lookSeconds` on, the flare is within the missile's class's lock cone of the ship as the missile sees it; else the class's IR `seduce` × the aspect
 * (1 head-on or abeam … `aspectTail` chased from dead astern, the engine in the seeker's view).
 */
export function seduceChance(state: Pick<GameState, 'planets' | 'config'>, m: Pick<MissileFlight, 'pos' | 'vel' | 'cls'>, ship: { pos: Vec3; vel: Vec3 }, flare: Pick<DecoyFlare, 'pos' | 'vel' | 'planetIndex'>): number {
  const t = C.lookSeconds, f = flareAt(state, flare, t);
  if (!f) return 0;
  const line0 = vecSub(ship.pos, m.pos), range0 = vecLength(line0), closing = range0 > 1e-9 ? vecDot(vecSub(m.vel, ship.vel), line0) / range0 : 0;
  if (closing > 0 && range0 / closing <= t) return 0;   // (popped too late: the missile is on the ship before the flare is clear)
  const eye = vecAdd(m.pos, vecScale(m.vel, t)), s = vecAdd(ship.pos, vecScale(ship.vel, t));
  const toS = vecSub(s, eye), toF = vecSub(f.pos, eye);
  if (vecLength(toS) < 1e-9 || vecLength(toF) < 1e-9) return 0;
  const apart = dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(toS), vecNormalize(toF)))));
  if (apart > MISSILE_CLASSES[m.cls ?? 'small'].lock.cone) return 0;
  const sp = vecLength(ship.vel), line = vecSub(ship.pos, m.pos);
  const behind = sp > 1e-9 && vecLength(line) > 1e-9 ? vecDot(vecScale(ship.vel, 1 / sp), vecNormalize(line)) : 0;   // (1: the missile behind the ship, chasing)
  const aspect = 1 - (1 - C.aspectTail) * Math.max(0, behind);
  return C.seduce[m.cls ?? 'small'] * aspect;
}

export type CountermeasureEvent =
  | { kind: 'decoyPop'; flight: number; team: number; planetIndex: number; at: Vec3; decoy: number; missile: number; taken: boolean }
  | { kind: 'shipIntercept'; flight: number; team: number; planetIndex: number; at: Vec3; missile: number; hit: boolean };

/** The missiles after flight `f` (an enemy's, still chasing it) within `reach` of `at`, nearest first (ties by id). */
function chasing(live: readonly MissileFlight[], f: DropshipFlight, at: Vec3, reach: number): Array<{ m: MissileFlight; range: number }> {
  return live.filter(m => m.team !== f.team && m.target === f.id && m.planetIndex === f.planetIndex)
    .map(m => ({ m, range: vecLength(vecSub(m.pos, at)) })).filter(x => x.range <= reach)
    .sort((a, b) => a.range - b.range || a.m.id - b.m.id);
}

/**
 * One tick of the ships' countermeasures, before the missiles fly: the burnt-out flares dropped; each ship (in id order) a bot
 * flies pops a salvo at the nearest missile after it not yet answered, within reach, when its interval is up (the flare
 * released at its hull, ejected down and back; taken or not by `seduceChance`, rolled keyed); each gunship's turret bursts at
 * the nearest missile after it within its reach (point defence's chance; a kill takes the missile out). The state and events.
 */
export function stepCountermeasures(state: GameState, tick: number): { state: GameState; events: CountermeasureEvent[] } {
  const cfg = state.config;
  if (!defaultOn(cfg, 'countermeasures') && !defaultOn(cfg, 'shipTurrets')) return { state, events: [] };
  const events: CountermeasureEvent[] = [];
  let decoys = state.decoys, live = state.missiles?.live ?? [], flights = state.dropships?.flights ?? [];
  if (decoys?.live.some(d => (tick - d.tick) * PHYS_DT > C.burnSeconds)) decoys = { ...decoys, live: decoys.live.filter(d => (tick - d.tick) * PHYS_DT <= C.burnSeconds) };
  const person = personOf(state), seed = cfg.seed ?? '';
  if (live.length) {
    const fl = [...flights].sort((a, b) => a.id - b.id);
    for (const f0 of fl) {
      let f = f0;
      const pops = defaultOn(cfg, 'countermeasures') && (f.decoys ?? 0) > 0 && !f.piloted && (f.poppedTick === undefined || tick - f.poppedTick >= ticksOf(C.popSeconds));
      const shoots = defaultOn(cfg, 'shipTurrets') && !!f.turret && f.turret.left > 0 && tick >= f.turret.next;
      if (!pops && !shoots) continue;
      const now = flightNow(state, f, tick);
      if (!now || !now.flight?.kin) continue;   // (in the air only)
      if (pops) {
        const pick = chasing(live, f, now.pos, C.reachPeople * person).find(x => !x.m.flared);
        if (pick) {
          const up = vecNormalize(vecSub(now.pos, state.planets[f.planetIndex].position)), sp = vecLength(now.vel);
          const back = sp > 1e-9 ? vecScale(now.vel, -0.5 / sp) : { x: 0, y: 0, z: 0 };
          const vel = vecAdd(now.vel, vecScale(vecNormalize(vecSub(back, up)), C.ejectPeople * person));
          const id = DECOY_ID_BASE + (decoys?.next ?? 0);
          const flare: DecoyFlare = { id, team: f.team, planetIndex: f.planetIndex, flight: f.id, pos: now.pos, vel, tick };
          const p = seduceChance(state, pick.m, { pos: now.pos, vel: now.vel }, flare);
          const salvo = (pick.m.flared ?? 0) + 1;
          const taken = p > 0 && new SeededRandom(`${seed}-decoy-${pick.m.id}-${salvo}-${tick}`).next() < p;
          live = live.map(m => (m.id !== pick.m.id ? m : { ...m, flared: salvo, ...(taken ? { target: id, unseen: 0, decoyedOff: f.id } : {}) }));
          decoys = { live: [...(decoys?.live ?? []), flare], next: (decoys?.next ?? 0) + 1 };
          f = { ...f, decoys: f.decoys! - 1, poppedTick: tick };
          events.push({ kind: 'decoyPop', flight: f.id, team: f.team, planetIndex: f.planetIndex, at: now.pos, decoy: id, missile: pick.m.id, taken });
        }
      }
      if (shoots) {
        const T = GUNSHIP.turret, pick = chasing(live, f, now.pos, T.reachPeople * person)[0];
        if (pick) {
          const m = pick.m, rel = vecSub(m.pos, now.pos), speed = vecLength(m.vel);
          const crossCos = speed > 1e-9 && pick.range > 1e-9 ? -vecDot(m.vel, rel) / (speed * pick.range) : 1;
          const blast = (explosionBlastFor(personSetOf(state), 'law')?.radius ?? 3 * person) / person;
          const p = killChance(T.variant, 'rocket', blast, speed / person, crossCos, pick.range / person, 0);
          const hit = new SeededRandom(`${seed}-shipturret-${f.id}-${tick}`).next() < p;
          if (hit) live = live.filter(x => x.id !== m.id);
          f = { ...f, turret: { left: f.turret!.left - 1, next: tick + ticksOf(T.interval) } };
          events.push({ kind: 'shipIntercept', flight: f.id, team: f.team, planetIndex: f.planetIndex, at: m.pos, missile: m.id, hit });
        }
      }
      if (f !== f0) flights = flights.map(x => (x.id === f.id ? f : x));
    }
  }
  if (decoys === state.decoys && live === (state.missiles?.live ?? []) && flights === (state.dropships?.flights ?? [])) return { state, events };
  return {
    state: {
      ...state,
      ...(decoys ? { decoys } : {}),
      ...(state.missiles ? { missiles: { ...state.missiles, live } } : {}),
      ...(state.dropships ? { dropships: { ...state.dropships, flights } } : {}),
    },
    events,
  };
}
